import { Button } from 'blaze-ui/Button'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Upload5 } from 'blaze-ui/icons'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { filesKeys } from 'hooks/queries/files/filesKeys'

export const UploadButton = ({
  label = 'Upload',
  brandKitId,
  ...buttonProps
}: ButtonProps & { label?: string; brandKitId?: number }) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const uploadsProjectId = uploadsProjectQuery.data?.project?.id
  const imageLibrary = useCurrentImageLibrary()

  const projectKey = brandKitId && imageLibrary?.key ? imageLibrary.key : uploadsProjectId
  const createParams = brandKitId ? { brand_kit_id: brandKitId } : undefined

  const { fileInputRef, uploadImagesFromEvent, selectInput, isUploading } = useImportMediaFiles(false, () => {
    if (brandKitId) {
      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
      if (imageLibrary?.key) queryClient.invalidateQueries(['workspace', workspaceId, 'files', imageLibrary.key])
    } else {
      queryClient.invalidateQueries(filesKeys.partialUploadsInfinite(workspaceId))
    }
  })

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!projectKey) return
    uploadImagesFromEvent(event, projectKey, createParams)
  }

  return (
    <>
      <VisuallyHidden>
        <input
          aria-label="Upload images"
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleOnChange}
          accept="image/*,video/*"
        />
      </VisuallyHidden>
      <Button
        fullWidth
        size="md"
        frontIcon={Upload5}
        onPress={selectInput}
        isDisabled={isUploading || !projectKey}
        aria-label="Upload Files"
        {...buttonProps}
      >
        {isUploading ? 'Uploading...' : label}
      </Button>
    </>
  )
}
